/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type React from 'react';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import type ChipGroupViewProps from '@airasia-phoenix/react-native/components/ChipGroupView/types';
import type ChipViewProps from '@airasia-phoenix/react-native/components/ChipView/types';
import type { InputStepperProps } from '@airasia-phoenix/react-native/components/InputStepperView';
import type { View } from 'react-native';

export interface PassengerItemProps {
  primaryText: string;
  secondaryText: string;
  inputStepperProps?: InputStepperProps;
}

export interface SelectedValues {
  cabinClass: CabinClassType;
  adult: number;
  child: number;
  infant: number;
}

export interface ChipViewWithValue extends ChipViewProps {
  value?: CabinClassType;
}

export interface ChipGroupViewPropsWithValue extends ChipGroupViewProps {
  chipViewProps: ChipViewWithValue[];
}

// Selection view props (your existing)
export interface PassengerClassSelectionProps {
  adultPassengerItemProps: PassengerItemProps;
  childPassengerItemProps: PassengerItemProps;
  infantPassengerItemProps: PassengerItemProps;
  maxTotal: number;
  cabinClassText: string;
  chipGroupViewProps: ChipGroupViewPropsWithValue;
  onChange?: (selectedValues: SelectedValues) => void;

  adultCount: number;
  childCount: number;
  infantCount: number;
  cabinClass: CabinClassType;
  hideCabinClass?: boolean;
}

// Field view props (parent-controlled state)
export interface PassengerClassFieldProps {
  adultCount: number;
  updateAdultCount: (adultCount: number) => void;

  childCount: number;
  updateChildCount: (childCount: number) => void;

  infantCount: number;
  updateInfantCount: (infantCount: number) => void;

  cabinClass: CabinClassType;
  updateCabinClass: (cabinClass: CabinClassType) => void;

  autoSearch?: (selectedValues: SelectedValues) => void;
  disabled?: boolean;
  hideCabinClass?: boolean;
}

export type AnchorRect = {
  x: number;
  y: number;
  width: number;
  height: number;
} | null;

// BottomSheet/Modal view props (draft inside)
export interface PassengerClassBottomSheetViewProps {
  // web
  isModalVisible: boolean;
  onRequestCloseModal: (e?: any) => void;

  // native
  isSheetOpen: boolean;
  onSheetDidClose: (e?: any) => void;
  bottomSheetViewRef: React.RefObject<BottomSheetRefs | null>;

  // committed values
  adultCount: number;
  childCount: number;
  infantCount: number;
  cabinClass: CabinClassType;

  // commit handlers
  updateAdultCount: (adultCount: number) => void;
  updateChildCount: (childCount: number) => void;
  updateInfantCount: (infantCount: number) => void;
  updateCabinClass: (cabinClass: CabinClassType) => void;

  // callback after done (to update TextField value)
  onDone: (selected: SelectedValues, e?: any) => void;
  anchorRef?: React.RefObject<View | null>;
  hideCabinClass?: boolean;
}

/* eslint-disable no-unused-vars */
export enum CabinClassType {
  ECONOMY = 'ECONOMY',
  BUSINESS = 'BUSINESS',
  PREMIUM_ECONOMY = 'PREMIUM_ECONOMY',
  FIRST_CLASS = 'FIRST_CLASS',
}
/* eslint-enable no-unused-vars */

export const CABIN_CLASS_MAP = {
  economy: CabinClassType.ECONOMY,
  business: CabinClassType.BUSINESS,
  premium_economy: CabinClassType.PREMIUM_ECONOMY,
  first_class: CabinClassType.FIRST_CLASS,
} as const;

export type CabinClassKey = keyof typeof CABIN_CLASS_MAP;

export interface PassengerCount {
  adultCount?: number;
  childCount?: number;
  infantCount?: number;
}
